import React from 'react';
import styled from 'styled-components';
import { Table } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { Passenger, PassengerType, DocumentType } from '../types';

interface PassengerInfoTableProps {
  passengers: Passenger[];
}

export const PassengerInfoTable: React.FC<PassengerInfoTableProps> = ({
  passengers,
}) => {
  const translations = useSelector(selectTranslations);

  const getPassengerTypeLabel = (type: PassengerType): string => {
    const labels = {
      ADT: translations?.['b2b.txt.adult'] || 'Người lớn',
      CHD: translations?.['b2b.txt.child'] || 'Trẻ em',
      INF: translations?.['b2b.txt.infant'] || 'Trẻ sơ sinh',
    };
    return labels[type];
  };

  const getDocumentTypeLabel = (type: DocumentType): string => {
    return type === DocumentType.CCCD ? 'CCCD' : (translations?.['b2b.txt.passport'] || 'Hộ chiếu');
  };

  const formatDate = (dateStr: string) => {
    return moment(dateStr).format('DD/MM/YYYY');
  };

  const columns = [
    {
      title: translations?.['b2b.txt.passengerSTT'] || 'STT',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => `#${index + 1}`,
    },
    {
      title: translations?.['b2b.txt.passengerName'] || 'Tên hành khách',
      key: 'name',
      render: (_: any, record: Passenger) =>
        `${record.title} ${record.firstName} ${record.lastName}`,
    },
    {
      title: translations?.['b2b.txt.passengerType'] || 'Đối tượng',
      dataIndex: 'passengerType',
      key: 'passengerType',
      render: (type: PassengerType) => getPassengerTypeLabel(type),
    },
    {
      title: translations?.['b2b.txt.dateOfBirth'] || 'Ngày sinh',
      dataIndex: 'dateOfBirth',
      key: 'dateOfBirth',
      render: (date: string) => formatDate(date),
    },
    {
      title: translations?.['b2b.txt.documentType'] || 'Loại giấy tờ',
      key: 'document',
      render: (_: any, record: Passenger) =>
        `${getDocumentTypeLabel(record.documentType)}: ${
          record.documentNumber
        }`,
    },
    {
      title: translations?.['b2b.txt.guardian'] || 'Người giám hộ',
      dataIndex: 'guardianName',
      key: 'guardianName',
      render: (name: string | undefined, record: Passenger) => {
        if (record.passengerType !== PassengerType.INF) return '-';
        return name || '-';
      },
    },
    {
      title: translations?.['b2b.txt.eTicket'] || 'Số vé điện tử',
      dataIndex: 'eTicketNumber',
      key: 'eTicketNumber',
      render: (num: string | undefined) => num || '-',
    },
  ];

  return (
    <TableWrapper>
      <StyledTable
        columns={columns}
        dataSource={passengers}
        pagination={false}
        // ensure we always provide a stable unique key, fall back to index
        rowKey={(record: Passenger, index?: number) => record.id || `pax-${index ?? 0}`}
        bordered
      />
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled(Table)`
  .ant-table {
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    background: #fafafa;
    font-weight: 600;
    color: #333;
  }

  .ant-table-tbody > tr > td {
    color: #666;
  }
` as typeof Table;
